import type {AxiosResponse as Response} from "axios"
import axios from "axios"
import router from "@/router"
import {useUserStore} from "@/stores/user"
import {$error} from "@/plugins/errorHandler"
import {ROUTES} from "@/router/paths"
import {type App} from "vue"
import {Capacitor} from '@capacitor/core'
import {Directory, Filesystem} from '@capacitor/filesystem'
import {Share} from '@capacitor/share'
import {errorLogger, requestLogger, responseLogger} from 'axios-logger'

export type AxiosResponse<T = object> = Response<T & { errors?: string[] }>

export interface AxiosPaginationResponse<ItemType> extends AxiosResponse {
  data: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    first_page_url: string
    last_page_url: string
    next_page_url?: string
    prev_page_url?: string
    path: string
    from: number
    to: number
    data: ItemType[]
    errors?: string[]
  }
}

export const $http = axios.create({
  headers: {
    common: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  },
  baseURL: `${import.meta.env.VITE_APP_URL}${import.meta.env.VITE_API_BASE_URL}`
})

$http.interceptors.request.use((config) => {
  if (localStorage.getItem("token")) {
    config.headers["Authorization"] = "Bearer " + localStorage.getItem("token")
  }

  if (config.data instanceof FormData) {
    config.headers['Content-Type'] = 'multipart/form-data'
  }

  return config
})

if (import.meta.env.DEV) {
  $http.interceptors.request.use(requestLogger, errorLogger)
  $http.interceptors.response.use(responseLogger, errorLogger)
}

$http.interceptors.response.use(
  function (response) {
    return response
  },
  function ({response, message}) {
    if (!response) {
      return Promise.reject({status: 500, data: {message}})
    }
    if (
      response.status === 401 ||
      response.data.message === "Authentication is required to continue"
    ) {
      if (router.currentRoute.value.name !== ROUTES.LOGIN) {
        const userStore = useUserStore()
        userStore.logout().then(() => {
          router
            .push({name: ROUTES.LOGIN, query: {to: router.currentRoute.value.fullPath}})
            .catch((e: Error) => e)
        })
      }
    }
    return Promise.reject(response)
  }
)

$http.download = async function <T = any, R = AxiosResponse<T>>(url: string, params = {}, method = "get"): Promise<R> {
  const response = await this.request({
    url,
    method,
    params: method === "get" ? params : null,
    data: method !== "get" ? params : null,
    responseType: "blob"
  }).catch(e => e)

  if (response.status > 299) {
    const jsonData = await new Response(response.data).json()
    if ($error(response.status, jsonData.message)) return response
  }

  // Get File Name
  const headerLine = response.headers["Content-Disposition"] || response.headers["content-disposition"] || ""
  let startFileNameIndex = headerLine.indexOf("filename=")
  let filename = "downloaded-file"
  if (startFileNameIndex !== -1) {
    startFileNameIndex += 9
    const endFileNameIndex = headerLine.length
    filename = headerLine.substring(startFileNameIndex, endFileNameIndex).replace(/"/g, "")
  }

  // Detect Capacitor/iOS
  if (Capacitor.isNativePlatform()) {
    // Convert blob to base64 string
    const blob = response.data
    const reader = new FileReader()
    const base64: string = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
    const base64Data = base64.substring(base64.indexOf(',') + 1)
    // Save to Filesystem as base64 string
    const fileResult = await Filesystem.writeFile({
      path: filename,
      data: base64Data,
      directory: Directory.Documents
      // Do NOT set encoding for binary files
    })
    // Share the file using the returned URI
    try {
      await Share.share({
        title: filename,
        url: fileResult.uri
      })
    } catch (e) {
      // Ignore error if sharing failed
    }
    // Delete the file after sharing to free device space
    try {
      await Filesystem.deleteFile({
        path: filename,
        directory: Directory.Documents
      })
    } catch (e) {
      // Ignore error if file not found or already deleted
    }
  } else {
    // Download the file (browser)
    const href = URL.createObjectURL(response.data)
    const link = document.createElement("a")
    link.href = href
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
  }

  return response
}

export default {
  install(app: App) {
    app.config.globalProperties.$http = $http
  }
}
